import { useCallback, useState } from "react";

import { normalizeRowError } from "helpers/formErrors";

/**
 * Maps server 422 errors onto an RHF form instance.
 *
 * Pass the `methods` returned by `useBaseForm`. Call `applyServerError(err)`
 * inside an `onSubmit` catch — each per-field error becomes a
 * `methods.setError(field, { type: "server", message })` call. The generic
 * portion (no field, or `_` key) surfaces via `genericError`.
 *
 * `clearGenericError` lets the form dismiss the banner without losing the
 * underlying error context.
 */
const useFormErrors = methods => {
  const [genericError, setGenericError] = useState(null);

  const applyServerError = useCallback(
    err => {
      const { fieldErrors, genericError: nextGeneric } = normalizeRowError(err);
      for (const [field, { message }] of Object.entries(fieldErrors)) {
        methods.setError(field, { type: "server", message });
      }
      setGenericError(nextGeneric ?? null);
      return { fieldErrors, genericError: nextGeneric ?? null };
    },
    [methods]
  );

  const clearGenericError = useCallback(() => setGenericError(null), []);

  return { genericError, applyServerError, clearGenericError };
};

export default useFormErrors;
